import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Button, Modal, message, Divider, Space } from 'antd';

import { queryClient } from '@/Root';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import AccessoriesModalGeneralForm from '@/components/Operational/Accessories/General/ModalForm';
import { getTableScrollX } from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { AppPageHeader } from '@/components/_common';
import AccessoriesExtraFilters from '@/components/Operational/Accessories/General/AccessoriesExtraFilters';
import {
  useDeleteVehicleAccessory,
  useVehicleAccessories,
} from '@/core/domain/people/Accessory/Accessory.hooks';
import {
  ListVehicleAccessoryQuery,
  VehicleAccessory,
} from '@/core/domain/people/Accessory/Accessory.types';
import { useDebounce } from '@/hooks';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function AccessoriesPage({ route }: PageProps) {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useState<ListVehicleAccessoryQuery>({
    order: 'DESC',
    active: true,
    ...convertParams<ListVehicleAccessoryQuery>({
      description: 'string',
      active: 'boolean',
      checkList: 'boolean',
      order: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useVehicleAccessories(queryDebounce);

  usePersistSearchParams(queryDebounce);

  const accessories = flatInfiniteData(data);

  const deleteMutation = useDeleteVehicleAccessory();

  const [modalGeneralVisible, setModalGeneralVisible] = useState(false);
  const [accessorySelected, setAccessorySelected] = useState<number>();

  useEffect(() => {
    if (accessorySelected) {
      toggleModalGeneral();
    }
  }, [accessorySelected]);

  const toggleModalGeneral = () => {
    if (modalGeneralVisible) {
      setAccessorySelected(undefined);
    }

    setModalGeneralVisible(!modalGeneralVisible);
  };

  async function removeAcessoryFetch(accessoryId: number) {
    try {
      // Fetch Service to remove group item.
      await deleteMutation.mutateAsync(accessoryId);

      await queryClient.refetchQueries('accessories');

      // Fetch Service to remove accessory item.
      message.success(
        intl.formatMessage({
          id: 'accessories.modal.remove.message.success',
        }),
      );
    } catch {}
  }

  function removeAccessoryModal(accessory: VehicleAccessory) {
    Modal.confirm({
      title: intl.formatMessage({ id: 'accessories.modal.remove.title' }),
      content: `${intl.formatMessage({
        id: 'accessories.modal.remove.content',
      })} "${accessory.description}"`,
      onOk: async () => removeAcessoryFetch(accessory.id),
      okText: intl.formatMessage({
        id: 'accessories.modal.remove.button.ok',
      }),
      okType: 'danger',
    });
  }

  const columns: ColumnsType<VehicleAccessory> = [
    {
      key: 'description',
      dataIndex: 'description',
      title: intl.formatMessage({ id: 'accessories.general.form.description' }),
      ellipsis: true,
      width: 250,
    },
    {
      key: 'checkList',
      dataIndex: 'checkList',
      title: 'Checklist',
      ellipsis: true,
      width: 100,
      render: (value) => (
        <FormattedMessage id={`commons.options.${value ? 'yes' : 'no'}`} />
      ),
    },
    {
      key: 'matrixId',
      dataIndex: 'matrixId',
      title: 'Padrão',
      ellipsis: true,
      width: 100,
      render: (matrixId) => (!!matrixId ? 'Não' : 'Sim'),
    },
    {
      key: 'active',
      dataIndex: 'active',
      title: 'Ativo',
      ellipsis: true,
      width: 100,
      render: (active) => (!active ? 'Não' : 'Sim'),
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      ellipsis: true,
      width: 100,
      align: 'right',
      fixed: 'right',
      // TODO: Corrigir os a tipagem da lista de acessórios
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_, accessory: any) => (
        <Space>
          <EditIcon
            iconProps={{ onClick: () => setAccessorySelected(accessory.id) }}
            disabled={
              !accessory.matrixId ||
              !hasPermissions([Authorities.REGISTER_ACCESSORY_EDIT])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => removeAccessoryModal(accessory)}
            disabled={
              !accessory.matrixId ||
              !hasPermissions([Authorities.REGISTER_ACCESSORY_DELETE])
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Helmet
        title={intl.formatMessage({
          id: 'layout.menu.operational.accessories',
        })}
      />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <Button
            block
            type="primary"
            onClick={toggleModalGeneral}
            disabled={!hasPermissions([Authorities.REGISTER_ACCESSORY_ADD])}
          >
            {intl.formatMessage({
              id: 'accessories.page.button.new',
            })}
          </Button>
        }
      >
        <AppPageHeader
          searchValue={queryParams.description}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, description: value }))
          }
          extraFilters={
            <AccessoriesExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<VehicleAccessory>
          columns={columns}
          dataSource={accessories}
          rowKey={(row) => row.id}
          loading={isFetching}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </TabContentWrapper>

      <AccessoriesModalGeneralForm
        visible={modalGeneralVisible}
        accessory={accessorySelected}
        onCancel={toggleModalGeneral}
      />
    </div>
  );
}

export default AccessoriesPage;
